import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './LivekitRoom.css';
import ChatPanel from './ChatPanel';
import { getEngineHost, WS_PROTOCOL } from '../config/environment';

function LivekitRoom({ user }) {
  const { roomName: urlRoomName } = useParams();
  const navigate = useNavigate();
  const [roomName, setRoomName] = useState(urlRoomName || 'dev-room');
  const [currentRoom, setCurrentRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [speakingParticipants, setSpeakingParticipants] = useState(new Set());
  const [isChatVisible, setIsChatVisible] = useState(true);
  const [isRoomMuted, setIsRoomMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [videoParticipants, setVideoParticipants] = useState(new Map());
  
  // Keep a reference to LiveKit components
  const livekitRef = useRef({
    room: null,
    audioWs: null,
    livekitAudioWs: null,
    keepAliveInterval: null,
    livekitKeepAliveInterval: null
  });

  // Auto-join room if coming from dashboard with a room name
  useEffect(() => {
    if (urlRoomName && !currentRoom && !isLoading) {
      joinRoom();
    }
  }, [urlRoomName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync isMuted state with window property for audio processor access
  useEffect(() => {
    // Make isMuted state accessible to audio processor
    window.audioMuted = isMuted;
    console.log(`[Audio] Mute state changed: ${isMuted}`);
  }, [isMuted]);

  // Debug participant updates
  useEffect(() => {
    if (participants.length > 0) {
      console.log('[Audio] Current participants:', participants.map(p => ({
        name: p.name,
        id: p.id,
        isMuted: p.isMuted,
        isLocal: p.isLocal
      })));
    }
  }, [participants]);

  // Ensure proper initialization of mute state when joining a room
  useEffect(() => {
    if (currentRoom) {
      // Reset mute state when joining a new room
      setIsMuted(false);
      window.audioMuted = false;
    }
  }, [currentRoom]);

  // Log user role information
  useEffect(() => {
    console.log("Current user role:", user.role);
    console.log("Is user admin?", user.role === 'admin');
  }, [user]);

  // Toggle mute all function for admins
  const toggleMuteAll = () => {
    if (!livekitRef.current.audioWs || !currentRoom) return;
    
    // Determine the command based on current global state - we want to toggle
    const newMuteState = !isRoomMuted;
    const command = newMuteState ? 'muteAll' : 'unmuteAll';

    console.log("[MUTE-DEBUG] Sending command:", command, "for room", roomName);
    
    try {
      // Send command via WebSocket
      livekitRef.current.audioWs.send(JSON.stringify({
        type: 'command',
        command: command,
        roomName: roomName,
        sender: user.name,
        senderId: user.id,
        isAdmin: user.role === 'admin',
        timestamp: Date.now()
      }));
      
      console.log(`[Audio] Sent ${command} command to room:`, roomName);

      // Add local fallback handling until server properly broadcasts
      setTimeout(() => {
        console.log("[MUTE-DEBUG] Simulating command response locally");
        const commandMsg = {
          type: 'command',
          command: newMuteState ? 'muteAll' : 'unmuteAll',
          isAdmin: true
        };
        processCommand(commandMsg);
      }, 100);
      
      // Update room mute state
      setIsRoomMuted(newMuteState);
      
      // Update local state to match
      setIsMuted(newMuteState);
      
      // Mute local participant in LiveKit
      if (livekitRef.current.room) {
        livekitRef.current.room.localParticipant.setMicrophoneEnabled(!newMuteState);
      }

      // Also update all participants in the participants list
      setParticipants(prev => prev.map(p => ({...p, isMuted: newMuteState})));
    } catch (err) {
      console.error(`Error sending ${command} command:`, err);
      setError(`Error ${newMuteState ? 'muting' : 'unmuting'} all participants: ${err.message}`);
    }
  };

  // Process mute/unmute commands
  const processCommand = (data) => {
    if (data.type === 'command' && data.isAdmin) {
      if (data.command === 'muteAll') {
        setIsMuted(true);
        window.audioMuted = true;
        if (livekitRef.current.room) {
          livekitRef.current.room.localParticipant.setMicrophoneEnabled(false);
        }
        // Update ALL participants in the list to show muted
        setParticipants(prev => prev.map(p => ({...p, isMuted: true})));
      } else if (data.command === 'unmuteAll') {
        setIsMuted(false);
        window.audioMuted = false;
        if (livekitRef.current.room) {
          livekitRef.current.room.localParticipant.setMicrophoneEnabled(true);
        }
        // Update ALL participants in the list to show unmuted
        setParticipants(prev => prev.map(p => ({...p, isMuted: false})));
      }
    }
  };

  // Check if Bella (🤖) is present in the room
  const checkAgentPresence = (room) => {
    if (!room) return false;
    
    let participants = [];
    
    if (room.remoteParticipants) {
      if (typeof room.remoteParticipants.forEach === 'function') {
        room.remoteParticipants.forEach(p => participants.push(p));
      } else if (room.remoteParticipants instanceof Map) {
        participants = Array.from(room.remoteParticipants.values());
      }
    } else if (room.participants) {
      room.participants.forEach(p => participants.push(p));
    }
    
    for (const participant of participants) {
      if (participant.identity === 'Bella (🤖)') {
        console.log('Found Bella (🤖) in the room');
        return true;
      }
    }
    
    return false;
  };

  // Function removed - AI conversations should be started from Admin Dashboard only

  // Function to check conversation status
  const checkConversationStatus = async (roomName) => {
    try {
      const response = await fetch(`https://ai-twilio-3ac8.myjava.ai/api/conversation/status?id=${encodeURIComponent(roomName)}`);
      const data = await response.json();
      return data.active || false;
    } catch (error) {
      console.error('[Conversation] Error checking status:', error);
      return false;
    }
  };

  // Setup two WebSocket connections - one for audio streaming and one for LiveKit audio
  const setupWebSocketConnections = (roomName, room) => {
    // Close any existing connections
    if (livekitRef.current.audioWs && livekitRef.current.audioWs.readyState !== WebSocket.CLOSED) {
      livekitRef.current.audioWs.close();
    }
    
    if (livekitRef.current.livekitAudioWs && livekitRef.current.livekitAudioWs.readyState !== WebSocket.CLOSED) {
      livekitRef.current.livekitAudioWs.close();
    }
    
    // Clear any existing intervals
    if (livekitRef.current.keepAliveInterval) {
      clearInterval(livekitRef.current.keepAliveInterval);
    }
    
    if (livekitRef.current.livekitKeepAliveInterval) {
      clearInterval(livekitRef.current.livekitKeepAliveInterval);
    }
    
    window.roomActive = true;
    
    // FIRST CONNECTION: General audio streaming WebSocket
    try {
      // Use environment-based protocol and host
      const wsUrl = `${WS_PROTOCOL}://${getEngineHost()}/audio-stream/${encodeURIComponent(roomName)}`;
      
      console.log('[Audio] Connecting to WebSocket:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('[Audio] WebSocket connected');
        
        // IMPORTANT: Send complete identity information including user ID and name
        const identity = room?.localParticipant?.identity || user.name;
        ws.send(JSON.stringify({
          type: 'register',
          roomName: roomName,    // Explicitly include room name in registration
          user_id: user.id,      // Include user ID for proper attribution
          participantName: user.name,  // Include user name for display
          clientId: `client-${Date.now()}-${Math.floor(Math.random() * 10000)}`
        }));
        console.log(`[Audio] Sent registration with identity: ${identity} for room: ${roomName}`);
        
        // Set up a keep-alive ping
        const keepAliveInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ 
              type: 'ping',
              timestamp: Date.now()
            }));
            console.log('[Audio] Sent keep-alive ping');
          } else {
            clearInterval(keepAliveInterval);
          }
        }, 15000);
        
        livekitRef.current.keepAliveInterval = keepAliveInterval;
      };
      
      ws.onclose = (event) => {
        console.log('[Audio] WebSocket closed:', event.code, event.reason);
        
        if (livekitRef.current.keepAliveInterval) {
          clearInterval(livekitRef.current.keepAliveInterval);
        }
        
        // Only try to reconnect if room is still active
        if (livekitRef.current.room && window.roomActive) {
          setTimeout(() => {
            if (window.roomActive) {
              console.log('[Audio] Attempting to reconnect WebSocket...');
              setupWebSocketConnections(roomName, room);
            }
          }, 3000);
        }
      };
      
      ws.onerror = (error) => {
        console.error('[Audio] WebSocket error:', error);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[Audio] WebSocket message received:', data.type || 'unknown type');
          
          if (data.type === 'audio') {
            console.log('[Audio] Received audio data from server');
          } else if (data.type === 'chat') {
            console.log('[Audio] Received chat message via WebSocket');
          } else if (data.type === 'command') {
            console.log(`[Audio] Received command: ${data.command} from ${data.sender || 'unknown'}`);
            
            if ((data.command === 'muteAll' || data.command === 'unmuteAll') && data.isAdmin) {
              const shouldMute = data.command === 'muteAll';
              console.log(`[Audio] Processing ${data.command} command, setting mute state to: ${shouldMute}`);
              
              // 1. Update local audio state
              setIsMuted(shouldMute);
              window.audioMuted = shouldMute;
              
              // 2. Update LiveKit state
              if (livekitRef.current.room) {
                livekitRef.current.room.localParticipant.setMicrophoneEnabled(!shouldMute);
              }
              
              // 3. Force update UI for ALL participants, not just local
              setParticipants(prev => {
                console.log(`[Audio] Updating UI for ${prev.length} participants to muted=${shouldMute}`);
                return prev.map(p => ({...p, isMuted: shouldMute}));
              });
              
              // 4. Set global mute state
              setIsRoomMuted(shouldMute);
              
              // 5. Broadcast own status for redundancy
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({
                  type: 'mute_status',
                  roomName: roomName,
                  userId: user.id,
                  userName: user.name,
                  isMuted: shouldMute,
                  timestamp: Date.now()
                }));
                console.log(`[Audio] Broadcasting own mute status after ${data.command}:`, shouldMute);
              }
            }
          } else if (data.type === 'mute_status') {
            console.log(`[Audio] Received mute status update from ${data.userName}: ${data.isMuted ? 'Muted' : 'Unmuted'}`);
            
            // Better matching logic for remote participants
            setParticipants(prevParticipants => {
              return prevParticipants.map(participant => {
                // More robust matching logic that handles different name formats
                // Strip "(You)" suffix for matching
                const participantName = participant.name.replace(' (You)', '');
                
                const isMatch = 
                  String(participant.id) === String(data.userId) || 
                  participantName === data.userName ||
                  participantName.includes(data.userName) ||
                  data.userName.includes(participantName);
                
                if (isMatch) {
                  console.log(`[Audio] Found participant match for ${data.userName}, updating mute status to ${data.isMuted}`);
                  return {
                    ...participant,
                    isMuted: data.isMuted
                  };
                }
                return participant;
              });
            });
          } else if (data.type === 'mute_status_ack') {
            console.log(`[Audio] Server acknowledged mute status for ${data.userName}: ${data.isMuted ? 'Muted' : 'Unmuted'}`);
          }
        } catch (error) {
          console.error('[Audio] Error parsing WebSocket message:', error);
        }
      };
      
      // Store the WebSocket
      livekitRef.current.audioWs = ws;
      // Make it available globally for the chat component
      window.existingWs = ws;
      
      // SECOND CONNECTION: LiveKit audio WebSocket
      setupLiveKitAudioConnection(roomName, room);
      
      return ws;
    } catch (error) {
      console.error('[Audio] Error setting up WebSocket:', error);
      setError(`WebSocket connection error: ${error.message}`);
      return null;
    }
  };
  
  // Setup the second WebSocket specifically for LiveKit audio
  const setupLiveKitAudioConnection = (roomName, room) => {
    try {
      // Use environment-based protocol and host
      const wsUrl = `${WS_PROTOCOL}://${getEngineHost()}/livekit-audio`;
      
      console.log('[Audio] Setting up LiveKit audio connection to:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('[Audio] LiveKit audio connection established');
        
        // Register this connection with the room
        ws.send(JSON.stringify({
          type: 'register',
          roomName: roomName,  // Explicitly include the room name
          participantId: user.id,
          user_id: user.id,    // Include user ID
          participantName: user.name // Include user name
        }));
        
        // Set up audio capture with higher quality for STT
        if (room && room.localParticipant) {
          console.log('[Audio] Setting up audio forwarding');
          setupMicrophoneCapture(ws, roomName, room);
        }
        
        // Set up a keep-alive ping
        const keepAliveInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ 
              type: 'ping',
              timestamp: Date.now()
            }));
            console.log('[Audio] Sent LiveKit audio keep-alive ping');
          } else {
            clearInterval(keepAliveInterval);
          }
        }, 15000);
        
        livekitRef.current.livekitKeepAliveInterval = keepAliveInterval;
      };
      
      ws.onclose = () => {
        console.log('[Audio] LiveKit audio connection closed');
        
        if (livekitRef.current.livekitKeepAliveInterval) {
          clearInterval(livekitRef.current.livekitKeepAliveInterval);
        }
        
        // Only try to reconnect if room is still active
        if (livekitRef.current.room && window.roomActive) {
          setTimeout(() => {
            if (window.roomActive) {
              setupLiveKitAudioConnection(roomName, room);
            }
          }, 3000);
        }
      };
      
      ws.onerror = (error) => {
        console.error('[Audio] LiveKit audio connection error:', error);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[Audio] LiveKit audio message received:', data.type || 'unknown');
          
          if (data.type === 'command') {
            console.log(`[Audio] Received command: ${data.command} from ${data.sender || 'unknown'}`);
            
            if ((data.command === 'muteAll' || data.command === 'unmuteAll') && data.isAdmin) {
              const shouldMute = data.command === 'muteAll';
              console.log(`[Audio] Processing ${data.command} command, setting mute state to: ${shouldMute}`);
              
              // 1. Update local audio state
              setIsMuted(shouldMute);
              window.audioMuted = shouldMute;
              
              // 2. Update LiveKit state
              if (livekitRef.current.room) {
                livekitRef.current.room.localParticipant.setMicrophoneEnabled(!shouldMute);
              }
              
              // 3. Force update UI for ALL participants, not just local
              setParticipants(prev => {
                console.log(`[Audio] Updating UI for ${prev.length} participants to muted=${shouldMute}`);
                return prev.map(p => ({...p, isMuted: shouldMute}));
              });
              
              // 4. Set global mute state
              setIsRoomMuted(shouldMute);
            }
          } else if (data.type === 'mute_status') {
            console.log(`[Audio] Received mute status update from ${data.userName}: ${data.isMuted ? 'Muted' : 'Unmuted'}`);
            
            // Better matching logic for remote participants
            setParticipants(prevParticipants => {
              return prevParticipants.map(participant => {
                // More robust matching logic that handles different name formats
                // Strip "(You)" suffix for matching
                const participantName = participant.name.replace(' (You)', '');
                
                const isMatch = 
                  String(participant.id) === String(data.userId) || 
                  participantName === data.userName ||
                  participantName.includes(data.userName) ||
                  data.userName.includes(participantName);
                
                if (isMatch) {
                  console.log(`[Audio] Found participant match for ${data.userName}, updating mute status to ${data.isMuted}`);
                  return {
                    ...participant,
                    isMuted: data.isMuted
                  };
                }
                return participant;
              });
            });
          }
        } catch (error) {
          console.error('[Audio] Error parsing LiveKit audio message:', error);
        }
      };
      
      // Store the connection
      livekitRef.current.livekitAudioWs = ws;
      
    } catch (error) {
      console.error('[Audio] Error setting up LiveKit audio connection:', error);
    }
  };

  // Optimized microphone capture function with per-event agent presence check
  const setupMicrophoneCapture = (ws, roomName, room) => {
    console.log('[Audio] Setting up microphone with per-event agent checking');
    
    if (window.microphoneStream) {
      console.log('[Audio] Stopping existing microphone stream');
      window.microphoneStream.getTracks().forEach(track => track.stop());
    }
    
    try {
      // Request high-quality audio specifically for STT
      navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
          sampleSize: 16,
          latency: 0
        } 
      })
      .then(stream => {
        // If room is not active anymore, stop everything now
        if (!window.roomActive) {
          console.log('[Audio] Room no longer active, stopping all audio setup');
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        // Store reference to stream for cleanup
        window.microphoneStream = stream;
        console.log('[Audio] Got high-quality microphone stream for STT');
        
        // Process audio data with 16kHz sampling
        const audioContext = new (window.AudioContext || window.webkitAudioContext)({sampleRate: 16000});
        // Store reference to context for cleanup
        window.audioContext = audioContext;
        
        const source = audioContext.createMediaStreamSource(stream);
        // Store reference to source for cleanup
        window.audioSource = source;
        
        const processor = audioContext.createScriptProcessor(2048, 1, 1);
        // Store reference to processor for cleanup
        window.audioProcessor = processor;
        
        // Throttle to reduce bandwidth
        let lastSendTime = 0;
        const SEND_INTERVAL = 100; // Send every 100ms for smoother audio
        
        processor.onaudioprocess = function(e) {
          // FIRST CHECK: If room is not active, stop processing immediately
          if (!window.roomActive || !livekitRef.current.room) {
            return;
          }
          
          // NEW CHECK: Skip sending audio if muted
          if (window.audioMuted) {
            return;
          }
          
          // Check for other indicators
          if (!room || !room.localParticipant || ws.readyState !== WebSocket.OPEN) {
            return;
          }
          
          const now = Date.now();
          if (now - lastSendTime > SEND_INTERVAL) {
            lastSendTime = now;
            
            // Check if Bella (🤖) is in the room
            let agentPresent = checkAgentPresence(room);
            
            // REMOVED: We always send audio regardless of agent presence
            // The backend will handle whether to process it or not
            // This ensures audio is available when the agent joins
            
            // Get raw audio data
            const buffer = e.inputBuffer.getChannelData(0);
            
            // Convert to 16-bit PCM
            const dataArray = new Int16Array(buffer.length);
            let maxLevel = 0;
            for (let i = 0; i < buffer.length; i++) {
              // Scale Float32 to Int16 with proper limiting
              dataArray[i] = Math.max(-32768, Math.min(32767, Math.floor(buffer[i] * 32767)));
              maxLevel = Math.max(maxLevel, Math.abs(buffer[i]));
            }
            
            // Audio level monitoring (reduced verbosity)
            if (Math.random() < 0.05 && maxLevel > 0.1) {
              console.log('[Audio] Significant input detected');
            }
            
            // Convert to base64
            const buffer8 = new Uint8Array(dataArray.buffer);
            const binary = Array.from(buffer8).map(b => String.fromCharCode(b)).join('');
            const base64Data = btoa(binary);
            
            // Final check before sending
            if (window.roomActive && ws.readyState === WebSocket.OPEN && !window.audioMuted) {
              // IMPORTANT: Always include the roomName when sending audio
              ws.send(JSON.stringify({
                type: 'audio',
                audio: base64Data,
                format: 'pcm16',
                sampleRate: 16000,
                roomName: roomName
              }));
              
              // Audio chunk sent for STT processing (reduced logging)
            }
          }
        };
        
        // Connect the processor
        source.connect(processor);
        processor.connect(audioContext.destination);
        
        console.log('[Audio] Audio forwarding set up successfully');
      })
      .catch(err => {
        console.error('[Audio] Error accessing microphone:', err);
        setError('Microphone access error: ' + err.message);
      });
    } catch (err) {
      console.error('[Audio] Error setting up microphone capture:', err);
      setError('Error setting up audio: ' + err.message);
    }
  };

  // Function to join a LiveKit room
  const joinRoom = async () => {
    if (!roomName) {
      setError('Please enter a room name');
      return;
    }

    setIsLoading(true);
    setError('');
    
    // Reset mute state when joining a room
    setIsMuted(false);
    window.audioMuted = false;
    setIsRoomMuted(false);

    try {
      // Request token from server using authenticated endpoint
      const response = await fetch('/api/livekit/join', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ roomName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to join room');
      }

      const data = await response.json();
      console.log('[LiveKit] Join response:', data);
      
      // Make sure LiveKit is loaded
      await ensureLiveKitLoaded();
      
      // Connect to the room
      const room = new window.LiveKit.Room();
      
      // Set up event listeners
      room.on('participantConnected', (participant) => {
        console.log('Participant connected:', participant.identity);
        updateParticipantsList(room);
        
        // Set up speaking event listeners for the new participant
        participant.on('audioLevelChanged', (level) => {
          handleAudioLevelChange(participant, level);
        });
      });
      
      room.on('participantDisconnected', (participant) => {
        console.log('Participant disconnected:', participant.identity);
        updateParticipantsList(room);
        
        // Remove from speaking participants
        setSpeakingParticipants(prev => {
          const updated = new Set(prev);
          updated.delete(participant.sid);
          return updated;
        });
      });
      
      // Add trackSubscribed handler for audio and video playback
      room.on('trackSubscribed', (track, publication, participant) => {
        console.log('Track subscribed:', track.kind, 'from', participant.identity);
        if (track.kind === 'audio') {
          // Create an audio element for this track
          const audioElement = document.createElement('audio');
          audioElement.id = `audio-${participant.identity}`;
          audioElement.autoplay = true;
          
          // Add to document (hidden)
          audioElement.style.display = 'none';
          document.body.appendChild(audioElement);
          
          // Attach the track to the audio element
          track.attach(audioElement);
          console.log('Audio track attached for', participant.identity);
        } else if (track.kind === 'video') {
          // Handle video track
          console.log('Video track received from', participant.identity);
          setVideoParticipants(prev => {
            const newMap = new Map(prev);
            newMap.set(participant.sid, { participant, track });
            return newMap;
          });
        }
      });
      
      // Add trackUnsubscribed handler to clean up
      room.on('trackUnsubscribed', (track, publication, participant) => {
        if (track.kind === 'audio') {
          track.detach();
          const audioElement = document.getElementById(`audio-${participant.identity}`);
          if (audioElement) {
            document.body.removeChild(audioElement);
          }
        } else if (track.kind === 'video') {
          track.detach();
          setVideoParticipants(prev => {
            const newMap = new Map(prev);
            newMap.delete(participant.sid);
            return newMap;
          });
        }
      });
      
      // Listen for active speaker events
      room.on('activeSpeakersChanged', (speakers) => {
        const speakingIds = new Set(speakers.map(speaker => speaker.sid));
        setSpeakingParticipants(speakingIds);
      });
      
      // Connect to the LiveKit server
      console.log('[LiveKit] Attempting to connect with:', {
        url: data.url,
        roomName: data.roomName,
        tokenLength: data.token?.length,
        tokenPrefix: data.token?.substring(0, 50) + '...'
      });
      await room.connect(data.url, data.token);
      console.log('Connected to room:', room.name);
      
      // Set up speaking event for local participant
      room.localParticipant.on('audioLevelChanged', (level) => {
        handleAudioLevelChange(room.localParticipant, level);
      });
      
      // Publish local audio track
      try {
        const audioTrack = await window.LiveKit.createLocalAudioTrack({
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1
        });
        
        await room.localParticipant.publishTrack(audioTrack);
        console.log('Published local audio track');
      } catch (micError) {
        console.error('Error publishing microphone:', micError);
        setError('Microphone access error: ' + micError.message);
      }
      
      // Mark room as active in window for compatibility
      window.roomActive = true;

      // Set up WebSocket connections for audio
      setupWebSocketConnections(roomName, room);
      
      // NOTE: We do NOT set up direct AI audio streaming here
      // Audio should only come through the broadcaster publishing to LiveKit
      console.log('[Audio] Skipping direct AI audio setup - audio will come through broadcaster');
      
      // Save the room state
      livekitRef.current.room = room;
      setCurrentRoom(room);
      
      // Update the participants list
      updateParticipantsList(room);
      
    } catch (err) {
      console.error('Error joining room:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle audio level changes to detect speaking
  const handleAudioLevelChange = (participant, level) => {
    // Audio level is between 0-1, typically treat > 0.1 as speaking
    const isSpeaking = level > 0.1;
    
    setSpeakingParticipants(prev => {
      const updated = new Set(prev);
      if (isSpeaking) {
        updated.add(participant.sid);
      } else {
        updated.delete(participant.sid);
      }
      return updated;
    });
  };

  // Removed setupAIAudioStream - audio should only come through broadcaster

  // Function to leave the room
  const leaveRoom = () => {
    // Clean up flag
    window.roomActive = false;
    
    // Cleanup intervals
    if (livekitRef.current.keepAliveInterval) {
      clearInterval(livekitRef.current.keepAliveInterval);
      livekitRef.current.keepAliveInterval = null;
    }
    
    if (livekitRef.current.livekitKeepAliveInterval) {
      clearInterval(livekitRef.current.livekitKeepAliveInterval);
      livekitRef.current.livekitKeepAliveInterval = null;
    }
    
    // Cleanup audio elements
    document.querySelectorAll('audio[id^="audio-"]').forEach(el => {
      try {
        document.body.removeChild(el);
      } catch (e) {
        console.warn('Error removing audio element:', e);
      }
    });
    
    // Close WebSockets
    if (livekitRef.current.audioWs) {
      try {
        livekitRef.current.audioWs.close();
      } catch (e) {
        console.warn('Error closing WebSocket:', e);
      }
      livekitRef.current.audioWs = null;
    }
    
    if (livekitRef.current.livekitAudioWs) {
      try {
        livekitRef.current.livekitAudioWs.close();
      } catch (e) {
        console.warn('Error closing LiveKit WebSocket:', e);
      }
      livekitRef.current.livekitAudioWs = null;
    }
    
    // Clean up microphone
    if (window.microphoneStream) {
      window.microphoneStream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (e) {
          console.warn('Error stopping track:', e);
        }
      });
      window.microphoneStream = null;
    }
    
    // Clean up audio processing
    if (window.audioProcessor) {
      if (window.audioSource) {
        try {
          window.audioSource.disconnect(window.audioProcessor);
        } catch (e) {
          console.warn('Error disconnecting audio source:', e);
        }
      }
      try {
        window.audioProcessor.disconnect();
      } catch (e) {
        console.warn('Error disconnecting audio processor:', e);
      }
      window.audioProcessor = null;
    }
    
    if (window.audioContext && window.audioContext.state !== 'closed') {
      try {
        window.audioContext.close();
      } catch (e) {
        console.warn('Error closing audio context:', e);
      }
      window.audioContext = null;
    }
    
    // Disconnect from the room
    if (livekitRef.current.room) {
      try {
        livekitRef.current.room.disconnect();
      } catch (e) {
        console.warn('Error disconnecting from room:', e);
      }
      livekitRef.current.room = null;
      setCurrentRoom(null);
    }
    
    // Reset participants list
    setParticipants([]);
    // Reset speaking participants
    setSpeakingParticipants(new Set());
    // Reset mute states
    setIsMuted(false);
    setIsRoomMuted(false);
    // Reset video state
    setIsVideoEnabled(false);
    setVideoParticipants(new Map());
  };

  // Function to toggle video
  const toggleVideo = async () => {
    if (!livekitRef.current.room) return;
    
    try {
      const newVideoState = !isVideoEnabled;
      console.log(`[Video] Toggling video from ${isVideoEnabled} to ${newVideoState}`);
      
      if (newVideoState) {
        // Enable video
        const videoTrack = await window.LiveKit.createLocalVideoTrack({
          resolution: {
            width: 1280,
            height: 720,
            frameRate: 30,
          },
          facingMode: 'user'
        });
        
        await livekitRef.current.room.localParticipant.publishTrack(videoTrack);
        console.log('[Video] Published local video track');
        
        // Add local video to the video participants
        setVideoParticipants(prev => {
          const newMap = new Map(prev);
          newMap.set(livekitRef.current.room.localParticipant.sid, {
            participant: livekitRef.current.room.localParticipant,
            track: videoTrack,
            isLocal: true
          });
          return newMap;
        });
      } else {
        // Disable video
        const videoTrack = livekitRef.current.room.localParticipant.getTrackPublication(window.LiveKit.Track.Source.Camera);
        if (videoTrack) {
          livekitRef.current.room.localParticipant.unpublishTrack(videoTrack.track);
          console.log('[Video] Unpublished local video track');
          
          // Remove local video from the video participants
          setVideoParticipants(prev => {
            const newMap = new Map(prev);
            newMap.delete(livekitRef.current.room.localParticipant.sid);
            return newMap;
          });
        }
      }
      
      setIsVideoEnabled(newVideoState);
    } catch (err) {
      console.error('Error toggling video:', err);
      setError('Error toggling video: ' + err.message);
    }
  };

  // Function to toggle mute
  const toggleMute = () => {
    if (!livekitRef.current.room) return;
    
    try {
      const newMuteState = !isMuted;
      console.log(`[Audio] Toggling mute from ${isMuted} to ${newMuteState}`);
      
      // First update the state
      setIsMuted(newMuteState);
      window.audioMuted = newMuteState;
      
      // Update LiveKit's mute state
      livekitRef.current.room.localParticipant.setMicrophoneEnabled(!newMuteState);
      
      // Update participants list for local participant
      setParticipants(prev => prev.map(p => 
        p.isLocal ? {...p, isMuted: newMuteState} : p
      ));
      
      console.log(`[Audio] Toggled mute: ${newMuteState ? 'Muted' : 'Unmuted'}`);
      
      // Broadcast mute status to all clients in room
      if (livekitRef.current.audioWs && livekitRef.current.audioWs.readyState === WebSocket.OPEN) {
        livekitRef.current.audioWs.send(JSON.stringify({
          type: 'mute_status',
          roomName: roomName,
          userId: user.id,
          userName: user.name,
          isMuted: newMuteState,
          timestamp: Date.now()
        }));
        console.log('[Audio] Broadcast mute status:', newMuteState);
      }
    } catch (err) {
      console.error('Error toggling mute:', err);
      setError('Error toggling mute: ' + err.message);
    }
  };

  // Helper function to update participants list
  const updateParticipantsList = (room) => {
    if (!room) return;
    
    // Get local participant
    const participantsList = [];
    if (room.localParticipant) {
      participantsList.push({
        id: room.localParticipant.sid,
        name: `${room.localParticipant.identity} (You)`,
        isLocal: true,
        isMuted: isMuted // Initialize with current mute state
      });
    }
    
    // Get remote participants
    let remoteParticipants = [];
    
    if (room.participants && typeof room.participants.forEach === 'function') {
      room.participants.forEach(p => remoteParticipants.push(p));
    } else if (typeof room.getParticipants === 'function') {
      remoteParticipants = room.getParticipants();
    } else if (room.remoteParticipants) {
      if (typeof room.remoteParticipants.forEach === 'function') {
        room.remoteParticipants.forEach(p => remoteParticipants.push(p));
      } else if (room.remoteParticipants instanceof Map) {
        remoteParticipants = Array.from(room.remoteParticipants.values());
      }
    }
    
    // Add remote participants to the list
    remoteParticipants.forEach(participant => {
      participantsList.push({
        id: participant.sid,
        name: participant.identity,
        isLocal: false,
        isMuted: false // Initialize remote participants as unmuted
      });
    });
    
    setParticipants(participantsList);
    console.log('[Audio] Updated participants list:', participantsList);
  };

  // Helper function to ensure LiveKit is loaded
  const ensureLiveKitLoaded = async () => {
    if (window.LiveKit) return;
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = "https://cdn.jsdelivr.net/npm/livekit-client/dist/livekit-client.umd.js";
      script.onload = () => {
        if (window.LiveKit) {
          resolve();
        } else if (window.LivekitClient) {
          window.LiveKit = window.LivekitClient;
          resolve();
        } else {
          reject(new Error("LiveKit not found after loading"));
        }
      };
      script.onerror = () => reject(new Error("Failed to load LiveKit script"));
      document.head.appendChild(script);
    });
  };

  // Function to go back to dashboard
  const goToDashboard = () => {
    // Check if there's an active callback for this room
    const activeCallbackKey = `activeCallback_${roomName}`;
    const callbackData = localStorage.getItem(activeCallbackKey);
    let hasActiveCallback = false;
    
    if (callbackData) {
      try {
        const { timestamp } = JSON.parse(callbackData);
        const now = Date.now();
        const CALLBACK_EXPIRY_MINUTES = 10;
        const expiryTime = CALLBACK_EXPIRY_MINUTES * 60 * 1000;
        
        hasActiveCallback = (now - timestamp) <= expiryTime;
      } catch (e) {
        // Invalid data
        hasActiveCallback = false;
      }
    }
    
    if (hasActiveCallback && urlRoomName) {
      // Navigate with rejoin parameter if there was an active callback
      navigate(`/dashboard?rejoin=${encodeURIComponent(urlRoomName)}`);
    } else {
      navigate('/dashboard');
    }
  };

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      console.log('LivekitRoom component unmounting, cleaning up');
      leaveRoom();
    };
  }, []);

  // Function to toggle chat visibility
  const toggleChat = () => {
    setIsChatVisible(!isChatVisible);
  };

  // Render different content based on role
  if (user.role === 'waiting') {
    return (
      <div className="livekit-room">
        <h2>Access Pending</h2>
        <div className="waiting-status-message">
          <p>Thank you for your interest in our early trial..</p>
          <p>Your account is currently pending approval. We have received your request to participate and will get back to you shortly.</p>
          <p>Please check back later or contact support if you have any questions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="room-layout">
      <div className="livekit-room">
        <h2>{roomName}</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        {!currentRoom ? (
          <div className="setup-panel">
            <div className="form-group">
              <label htmlFor="roomName">Room Name:</label>
              <input 
                id="roomName" 
                type="text" 
                value={roomName} 
                onChange={(e) => setRoomName(e.target.value)} 
                className="form-control"
              />
            </div>
            <div className="form-group">
              <p className="participant-name">
                <strong>Your Name:</strong> {user.name}
                {user.role === 'admin' && <span className="admin-badge"> (Admin)</span>}
              </p>
            </div>
            <div className="btn-group">
              <button 
                onClick={joinRoom} 
                className="btn btn-primary" 
                disabled={isLoading}
              >
                {isLoading ? 'Joining...' : 'Join Room'}
              </button>
            </div>
          </div>
        ) : (
          <div className="active-room">
            <div className="room-header">
              <div className="header-left">
                {urlRoomName && (
                  <button 
                    onClick={goToDashboard}
                    className="back-button"
                  >
                    &laquo; Back to Dashboard
                  </button>
                )}
              </div>
              <div className="controls">
                <button 
                  onClick={toggleChat} 
                  className="btn btn-secondary"
                >
                  {isChatVisible ? 'Hide Chat' : 'Show Chat'}
                </button>
                <button 
                  onClick={toggleMute} 
                  className={`btn ${isMuted ? 'btn-success' : 'btn-warning'}`}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </button>
                <button 
                  onClick={toggleVideo} 
                  className={`btn btn-video ${!isVideoEnabled ? 'disabled' : ''}`}
                >
                  {isVideoEnabled ? 'Disable Video' : 'Enable Video'}
                </button>
                {user.role === 'admin' && (
                  <button 
                    onClick={toggleMuteAll} 
                    className={`btn ${isRoomMuted ? 'btn-success' : 'btn-danger'}`}
                  >
                    {isRoomMuted ? 'Unmute All' : 'Mute All'}
                  </button>
                )}
                <button 
                  onClick={leaveRoom} 
                  className="btn btn-danger"
                >
                  Leave Room
                </button>
              </div>
            </div>
            
            {/* Video Grid */}
            {videoParticipants.size > 0 && (
              <div className="video-grid">
                {Array.from(videoParticipants.values()).map(({ participant, track, isLocal }) => (
                  <VideoParticipant
                    key={participant.sid}
                    participant={participant}
                    track={track}
                    isLocal={isLocal}
                    isMuted={participants.find(p => p.id === participant.sid)?.isMuted || false}
                  />
                ))}
              </div>
            )}
            
            <div className="participants-panel">
              <h4>Participants</h4>
              <ul className="participants-list">
                {participants.length > 0 ? (
                  participants.map(participant => (
                    <li 
                      key={participant.id} 
                      className={`${participant.isLocal ? 'local-participant' : ''} ${speakingParticipants.has(participant.id) && !participant.isMuted ? 'speaking' : ''}`}
                    >
                      {participant.name}
                      {participant.isMuted ? (
                        <span className="muted-indicator">
                          <span className="muted-icon">🔇</span>
                        </span>
                      ) : speakingParticipants.has(participant.id) && (
                        <span className="speaking-indicator">
                          <span className="wave wave1"></span>
                          <span className="wave wave2"></span>
                          <span className="wave wave3"></span>
                        </span>
                      )}
                    </li>
                  ))
                ) : (
                  <li>No participants</li>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
      
      {currentRoom && isChatVisible && (
        <div className="chat-container">
          <ChatPanel roomName={roomName} user={user} />
        </div>
      )}
    </div>
  );
}

// Video Participant Component
function VideoParticipant({ participant, track, isLocal, isMuted }) {
  const videoRef = React.useRef(null);
  
  React.useEffect(() => {
    if (videoRef.current && track) {
      track.attach(videoRef.current);
      return () => {
        track.detach(videoRef.current);
      };
    }
  }, [track]);
  
  // Get initials for placeholder
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  
  return (
    <div className="video-participant">
      {track ? (
        <video
          ref={videoRef}
          className="video-element"
          autoPlay
          playsInline
          muted={isLocal}
        />
      ) : (
        <div className="no-video-placeholder">
          {getInitials(participant.identity || 'UN')}
        </div>
      )}
      <div className="participant-info">
        <span className="participant-name-overlay">
          {participant.identity} {isLocal && '(You)'}
        </span>
        {isMuted && <span className="video-muted-indicator">🔇</span>}
      </div>
    </div>
  );
}

export default LivekitRoom;